/**
 * The light/dark preference: what the user picked, and what that resolves to.
 * Mirrors the locale module (i18n/index.ts) — an explicit choice is remembered
 * and always wins; until one is made the app follows the device.
 */

import { defineStoredString } from '../utils/storage';

/** What the user can choose. 'system' follows the device's own setting. */
export type ColorMode = 'system' | 'light' | 'dark';

/** The two modes a theme can actually be built in. */
export type ResolvedColorMode = 'light' | 'dark';

/**
 * The options the settings dropdown offers, in order. Unlike the languages
 * (named in themselves), these are ordinary UI text and carry i18n keys.
 */
export const COLOR_MODES: { value: ColorMode; labelId: string }[] = [
    { value: 'system', labelId: 'settings.theme.system' },
    { value: 'light', labelId: 'settings.theme.light' },
    { value: 'dark', labelId: 'settings.theme.dark' }
];

/** Exported for `useStoredValue`: a switch in one tab reaches the others. */
export const colorModeValue = defineStoredString<ColorMode>({
    key: 'color_mode',
    allow: ['system', 'light', 'dark']
});

/** Remember an explicit choice, so it survives reloads. */
export function storeColorMode(mode: ColorMode): void {
    // Not fatal when it does not land: the choice then only lasts this session.
    colorModeValue.write(mode);
}

/** What an unset choice means: follow the device. */
export const DEFAULT_COLOR_MODE: ColorMode = 'system';
